import React from 'react';
import { ArrowLeft, Shield, Mail, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const lastUpdated = "December 24, 2024";
  const companyName = "SafetyMind AI";
  const contactEmail = "privacy@safetymindai.com";
  const websiteUrl = "https://safetymindai.com";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            Last updated: {lastUpdated}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Introduction</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              {companyName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our mobile 
              application and web platform (collectively, the "Service"). Please read this privacy policy carefully. 
              By using the Service, you consent to the practices described in this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Personal Information</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-3">When you register for an account, we collect:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Name and email address</li>
              <li>Company or organization name</li>
              <li>Job title and role</li>
              <li>Phone number (optional)</li>
              <li>Profile photograph (optional)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Safety and Work Data</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-3">To provide our safety management services, we collect:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Job Safety Analysis (JSA) records and hazard assessments</li>
              <li>Safety audit reports and findings</li>
              <li>Incident and near-miss reports</li>
              <li>Training records and certifications</li>
              <li>Safety Data Sheet (SDS) information</li>
              <li>Workplace photographs related to safety documentation</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Automatically Collected Information</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-3">When you use our Service, we automatically collect:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Device information (type, operating system, unique identifiers)</li>
              <li>Log data (access times, pages viewed, app crashes)</li>
              <li>Location data (only when explicitly permitted for job site features)</li>
              <li>Usage patterns and feature interactions</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">How We Use Your Information</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">We use the collected information to:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Provide, maintain, and improve our safety management Service</li>
              <li>Process and manage your account registration</li>
              <li>Generate AI-powered safety recommendations and hazard identification</li>
              <li>Create safety analytics and trend reports for your organization</li>
              <li>Send important notifications about safety alerts and updates</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Comply with legal obligations and safety regulations</li>
              <li>Detect, prevent, and address technical issues or fraud</li>
            </ul>
          </section>

          {/* AI and Data Processing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">AI-Powered Features</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">
              Our Service uses artificial intelligence to enhance safety management. This includes:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Automated hazard identification from task descriptions</li>
              <li>AI-generated safety recommendations and control measures</li>
              <li>Intelligent document analysis for SDS and safety manuals</li>
              <li>Predictive safety analytics and trend identification</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-3">
              AI processing is performed using industry-standard third-party AI services. Your data is processed 
              securely and is not used to train general AI models. All AI-generated content should be reviewed 
              by qualified safety professionals before implementation.
            </p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Information Sharing and Disclosure</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">We may share your information in the following circumstances:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Within Your Organization</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Safety data is shared within your organization according to the roles and permissions set by your 
              company administrators. Supervisors and admins can view worker safety records as part of normal 
              safety management operations.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Service Providers</h3>
            <p className="text-gray-600 dark:text-gray-300">
              We work with trusted third-party service providers who assist in operating our Service, including 
              cloud hosting, AI processing, and analytics. These providers are contractually obligated to protect 
              your data and use it only for the purposes we specify.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">Legal Requirements</h3>
            <p className="text-gray-600 dark:text-gray-300">
              We may disclose your information if required by law, regulation, legal process, or governmental 
              request, including to meet OSHA or other safety regulatory requirements.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-2">We Do NOT</h3>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Sell your personal information to third parties</li>
              <li>Share your data for third-party advertising purposes</li>
              <li>Use your safety data for purposes unrelated to our Service</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Data Security</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication with password hashing</li>
              <li>Role-based access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Secure cloud infrastructure with industry certifications</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-3">
              While we strive to protect your information, no method of transmission over the Internet or 
              electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Data Retention</h2>
            <p className="text-gray-600 dark:text-gray-300">
              We retain your personal information for as long as your account is active or as needed to provide 
              our Service. Safety records may be retained longer to comply with regulatory requirements (such as 
              OSHA recordkeeping requirements). You may request deletion of your account and personal data, 
              subject to any legal retention obligations.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Your Rights and Choices</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to or restrict certain processing activities</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-3">
              To exercise these rights, please contact us at <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a>.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Children's Privacy</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Our Service is designed for workplace safety management and is not intended for children under 
              the age of 16. We do not knowingly collect personal information from children under 16. If you 
              believe we have collected information from a child under 16, please contact us immediately.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">International Data Transfers</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Your information may be transferred to and processed in countries other than your country of 
              residence. These countries may have different data protection laws. By using our Service, you 
              consent to the transfer of your information to the United States and other countries where we 
              operate. We take steps to ensure your data receives adequate protection in accordance with this 
              Privacy Policy.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-600 dark:text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage 
              you to review this Privacy Policy periodically for any changes. Your continued use of the Service 
              after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
              <p className="text-gray-700 dark:text-gray-200 font-medium">{companyName}</p>
              <p className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a>
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                Website: <a href={websiteUrl} className="text-blue-600 hover:underline">{websiteUrl}</a>
              </p>
            </div>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">California Privacy Rights</h2>
            <p className="text-gray-600 dark:text-gray-300">
              If you are a California resident, you have additional rights under the California Consumer Privacy 
              Act (CCPA), including the right to know what personal information we collect, the right to delete 
              your personal information, and the right to opt-out of the sale of your personal information. We 
              do not sell personal information. To exercise your California privacy rights, please contact us 
              at <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a>.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-8">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
